import {LogLevel, Occupancy} from "@variocube/app-api";
import {
    Avatar,
    BoxAccess,
    classNames,
    layout,
    PushButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    typography,
    useBoxes,
    usePlatform,
    usePreventDeactivate,
    View,
    ViewContent,
    ViewHeader,
    ViewHeaderItem,
    ViewTitle
} from "@variocube/cube-ui";
import * as React from "react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {CubeCarrier, getDeliveryOccupancyContent} from "./types";
import {useAuthenticatedCarrier, useCarriers} from "./CarrierProvider";
import {useLocalization} from "../i18n";

interface CarrierRetrievalProps {
    autoCarrierRetrieval?: boolean;
    occupancies: Occupancy[];
    onDone: () => void;
}

export function CarrierRetrieval({occupancies, onDone, autoCarrierRetrieval}: CarrierRetrievalProps) {
    const [occupancy, setOccupancy] = useState<Occupancy>();
    const [retrievable, setRetrievable] = useState<Occupancy[]>(occupancies);
    const [startedRetrieval, setStartedRetrieval] = useState<boolean>(false);

    useEffect(() => {
        console.log({startedRetrieval})
    }, [startedRetrieval]);

    const handleRetrievalDone = useCallback(() => {
        if (occupancy) {
            setRetrievable(prev => prev.filter(o => o.uuid != occupancy.uuid));
        }
        setOccupancy(undefined);
    }, [occupancy]);

    if (occupancy) {
        return (
            <CarrierSingleRetrieval
                occupancy={occupancy}
                onDone={handleRetrievalDone}
            />
        );
    }

    return (
        <CarrierRetrievalList
            retrievable={retrievable}
            onDone={onDone}
            onRetrieve={setOccupancy}
            autoCarrierRetrieval={autoCarrierRetrieval}
            startedRetrieval={startedRetrieval}
            setStartedRetrieval={setStartedRetrieval}
        />
    )
}

interface CarrierRetrievalListProps {
    autoCarrierRetrieval?: boolean;
    retrievable: Occupancy[];
    onRetrieve: (occupancy: Occupancy) => void;
    onDone: () => void;
    startedRetrieval: boolean;
    setStartedRetrieval: (startedRetrieval: boolean) => void;
}

function CarrierRetrievalList({retrievable, onRetrieve, onDone, autoCarrierRetrieval, startedRetrieval, setStartedRetrieval}: CarrierRetrievalListProps) {
    const {t} = useLocalization();

    const handleRetrieve = useCallback((occupancy: Occupancy) => {
        setStartedRetrieval(true);
        onRetrieve(occupancy);
    }, [onRetrieve, setStartedRetrieval]);

    useEffect(() => {
        if (autoCarrierRetrieval && startedRetrieval) {
            handleRetrieve(retrievable[0]);
        }
    }, [autoCarrierRetrieval, retrievable, startedRetrieval]);


    return (
        <View>
            <ViewHeader>
                <ViewHeaderItem className={layout.marginYMedium} fill>
                    <ViewTitle>{t("Retrieval")}</ViewTitle>
                </ViewHeaderItem>
            </ViewHeader>
            <ViewContent>
                {retrievable.length > 0 && (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t("Box")}</TableCell>
                                <TableCell>{t("Parcel")}</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {retrievable.map(occupancy => (
                                <CarrierRetrievalRow
                                    key={occupancy.uuid}
                                    occupancy={occupancy}
                                    onRetrieve={handleRetrieve}
                                />
                            ))}
                        </TableBody>
                    </Table>
                )}
                {retrievable.length == 0 && (
                    <div className={classNames(layout.paddingLarge, typography.alignCenter)}>
                        <div className={classNames(layout.paddingLarge)}>
                            {t("NoRetrievableParcels")}
                        </div>
                    </div>
                )}
            </ViewContent>
        </View>
    )
}

interface CarrierRetrievalRowProps {
    occupancy: Occupancy;
    onRetrieve: (occupancy: Occupancy) => void;
}

function CarrierRetrievalRow({occupancy, onRetrieve}: CarrierRetrievalRowProps) {
    //const strings = useStrings(resources);
    const {t} = useLocalization();
    const handleRetrieve = useCallback(() => onRetrieve(occupancy), [onRetrieve, occupancy]);
    const delivery = getDeliveryOccupancyContent(occupancy);

    return (
        <TableRow>
            <TableCell>
                <Avatar size={1.5}>{occupancy.boxNumber}</Avatar>
            </TableCell>
            <TableCell>
                {delivery?.deliveryId}
            </TableCell>
            <TableCell className={typography.alignRight}>
                <PushButton primary onClick={handleRetrieve}>
                    {t("Retrieve")}
                </PushButton>
            </TableCell>
        </TableRow>
    )
}

interface CarrierSingleRetrievalProps {
    occupancy: Occupancy;
    onDone: () => void;
}

export function CarrierSingleRetrieval({occupancy, onDone}: CarrierSingleRetrievalProps) {
    const {t} = useLocalization();
    const platform = usePlatform();
    const boxes = useBoxes();
    const box = useMemo(() => boxes.find(box => box.number == occupancy.boxNumber), [boxes, occupancy]);

    const authenticatedCarrier = useAuthenticatedCarrier();
    const carriers = useCarriers();
    const cubeCarrier = useMemo<CubeCarrier|undefined>(() => {
        if(carriers && authenticatedCarrier) {
            return carriers[authenticatedCarrier];
        }
    }, [carriers, authenticatedCarrier]);

    usePreventDeactivate();

    const handleConfirm = useCallback(async () => {
        try {
            await platform.endOccupancy(occupancy.uuid, {
                gracePeriod: 180,
            });
            await platform.log(LogLevel.Audit, "Carrier retrieved", {occupancy});
        }
        catch (error) {
            console.error("Error ending occupancy", error);
        }
        onDone();
    }, [platform, occupancy, onDone]);

    if (box) {
        return (
            <BoxAccess
                boxes={box}
                platform={platform}
                onCancel={onDone}
                disableConfirm={cubeCarrier?.firstMile?.reopenCountdown ? cubeCarrier?.firstMile?.reopenCountdown <= 1 : false}
                onConfirm={handleConfirm}
                boxOpenTitle={t("PickupTitle")}
                boxOpenMessage={t("PickupMessage")}
                confirmTitle={t("ConfirmPickupTitle")}
                confirmMessage={t("ConfirmPickupMessage")}
                strings={t}
                autoConfirm={cubeCarrier?.firstMile?.reopenCountdown}
            />
        )
    }
    return null;
}